import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Plus, X, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MBManagement({ onBack }) {
  const { user } = useAuth();
  const [mbs, setMbs] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Modal form states (Ref Image 3)
  const [mbName, setMbName] = useState('');
  const [adminPhone, setAdminPhone] = useState(user?.phone || '');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMBs();
  }, []);

  const fetchMBs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/mb-config`);
      let list = res.data?.mbs;
      if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch {}
      }
      setMbs(Array.isArray(list) ? list : []);
      setUsersList(res.data?.users || []);
    } catch (err) {
      console.error('Failed to load MB configuration:', err);
      toast.error('Failed to load MB configuration');
    } finally {
      setLoading(false);
    }
  };

  // Calculate next MB default name (e.g. MB7 if max is MB6)
  const getNextMBDefaultName = () => {
    let maxNum = 12;
    mbs.forEach(m => {
      const match = (m.id || '').match(/^MB(\d+)$/i);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    return `MB${maxNum + 1}`;
  };

  const openCreateModal = () => {
    const nextName = getNextMBDefaultName();
    setMbName(nextName);
    setAdminPhone(user?.phone || '');
    setPassword('');
    setShowCreateModal(true);
  };

  const handleToggleHide = async (mbId) => {
    try {
      const res = await axios.post(`${API}/mb-config/toggle-hide`, { mb_id: mbId });
      if (res.data?.mbs) {
        setMbs(res.data.mbs);
        toast.success(`MB status updated`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update MB status');
    }
  };

  const handleCreateMB = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error('Password is required');
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/mb-config/create-mb`, {
        mb_name: mbName.trim(),
        admin_phone: adminPhone.trim(),
        password: password
      });
      if (res.data?.ok) {
        toast.success(`Created ${res.data.mb?.label || mbName} successfully!`);
        setShowCreateModal(false);
        setPassword('');
        if (res.data.mbs) setMbs(res.data.mbs);
        else fetchMBs();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create MB');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#f8fafc] text-gray-800 font-sans">
      
      {/* ─── Top Green Header Bar (Ref Image 2) ─── */}
      <div className="bg-[#10b981] text-white px-5 py-3.5 flex items-center justify-between shadow-md flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1 hover:bg-white/10 rounded-full transition-colors focus:outline-none cursor-pointer"
          >
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div>
            <h2 className="text-base font-bold tracking-wide">MB Management</h2>
            <p className="text-[11px] text-emerald-100 font-normal">Admin Control Center</p>
          </div>
        </div>
      </div>

      {/* ─── Main Content Container ─── */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 flex justify-center">
        <div className="w-full max-w-3xl space-y-6">
          
          {/* ─── Main Control Card (Ref Image 2) ─── */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-6">
            
            {/* Header + New MB Button */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-100 pb-5">
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Tag size={22} className="text-amber-500" weight="fill" />
                  MB Management
                </h3>
                <p className="text-xs text-slate-500 max-w-xl leading-relaxed">
                  Create new MBs or hide existing ones. Every change needs your password verification. Mapped users to each MB are displayed below.
                </p>
              </div>

              <button
                onClick={openCreateModal}
                className="px-4 py-2.5 bg-[#4f46e5] hover:bg-[#4338ca] text-white text-xs font-bold rounded-lg shadow-sm transition-colors flex items-center justify-center gap-1.5 flex-shrink-0 cursor-pointer"
              >
                <Plus size={16} weight="bold" /> + New MB
              </button>
            </div>

            {/* MBs List */}
            {loading ? (
              <div className="text-center py-8 text-slate-400 text-xs">Loading MB segments...</div>
            ) : mbs.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-xs">No MB segments found.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {mbs.map((mb) => {
                  const isHidden = mb.hidden === true;
                  
                  // Find mapped users for this MB
                  const mappedUsers = usersList.filter(u => {
                    if (!u.assigned_mbs || !Array.isArray(u.assigned_mbs)) return false;
                    return u.assigned_mbs.some(m => String(m).toUpperCase() === String(mb.id).toUpperCase());
                  });

                  return (
                    <div key={mb.id} className="flex flex-col sm:flex-row sm:items-center justify-between py-3.5 px-2 hover:bg-slate-50/50 rounded-lg transition-colors gap-3">
                      <div className="space-y-1">
                        <span className="font-bold text-slate-800 text-sm block">{mb.label || mb.id}</span>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[11px] text-slate-400 font-medium">Mapped Users:</span>
                          {mappedUsers.length === 0 ? (
                            <span className="text-[11px] text-slate-400 italic">None</span>
                          ) : (
                            mappedUsers.map(u => (
                              <span key={u.id} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                {u.username || u.phone} ({u.role?.replace('_handler', '') || u.role})
                              </span>
                            ))
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleToggleHide(mb.id)}
                        className={`px-4 py-1 border text-xs font-bold rounded-full transition-colors cursor-pointer self-start sm:self-auto ${
                          isHidden
                            ? 'border-[#10b981] text-[#10b981] hover:bg-emerald-50'
                            : 'border-[#f97316] text-[#f97316] hover:bg-orange-50'
                        }`}
                      >
                        {isHidden ? 'Unhide' : 'Hide'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

          </div>

        </div>
      </div>

      {/* ─── CREATE MB POPUP MODAL (Ref Image 3) ─── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl space-y-4 border border-slate-200 font-sans">
            
            {/* Modal Header */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Create {mbName || 'MB'}</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Step 1 of 3 · Password</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 hover:bg-slate-100 rounded-full transition-colors text-slate-400 focus:outline-none"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateMB} className="space-y-4 pt-1">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">MB Name</label>
                <input
                  type="text"
                  required
                  value={mbName}
                  onChange={(e) => setMbName(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-bold text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. MB13"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Admin phone</label>
                <input
                  type="text"
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-slate-400 font-medium"
                  placeholder="e.g. 1558089654"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter admin password"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-[#4f46e5] hover:bg-[#4338ca] text-white font-bold text-sm rounded-xl shadow-md transition-colors cursor-pointer disabled:opacity-50"
                >
                  {submitting ? 'Verifying...' : 'Verify password → Next'}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
}
